import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import LogATicketModal from "../Modals/LogATicketModal";

const PORTALS = {
  owner: {
    label: "OWNER PORTAL",
    route: "/owner-portal",
    storageKey: "ownerPortalActiveTab",
    access: ["owner"],
    tabs: [
      { key: "accounts", value: "accounts", label: "Accounts" },
      { key: "tenant", value: "tenant", label: "Tenant Accounts" },
      { key: "buildings", value: "buildings", label: "Buildings" },
      { key: "tickets", value: "tickets", label: "Tickets" },
    ],
  },
  trustee: {
    label: "TRUSTEE PORTAL",
    route: "/trustee-portal",
    storageKey: "trusteePortalActiveTab",
    access: ["trustee"],
    tabs: [
      { key: "approvals", value: "approvals", label: "Approvals" },
      { key: "buildings", value: "buildings", label: "Buildings" },
      { key: "tickets", value: "tickets", label: "Tickets" },
    ],
  },
  rental: {
  label: "RENTAL PORTAL",
  route: "/rental-portal",
  storageKey: "rentalPortalActiveTab",
  legacyStorageKey: "rental_portal_mode",
  access: ["landlord", "tenant"],
  tabs: [
    { key: "landlord", value: "landlord", label: "Landlord View" },
    { key: "tenant", value: "tenant", label: "Tenant View" },
  ],
},
  contractor: {
    label: "CONTRACTOR PORTAL",
    route: "/contractor-portal",
    storageKey: "contractorPortalActiveTab",
    access: ["contractor"],
    tabs: [
      { key: "general", value: "General", label: "General" },
      { key: "accounts", value: "My Accounts", label: "My Accounts" },
      { key: "legal-files", value: "Legal Files", label: "Legal Files" },
      { key: "quotes", value: "My Quotes", label: "My Quotes" },
      { key: "jobs", value: "My Jobs", label: "My Jobs" },
      { key: "payments", value: "My Payments", label: "My Payments" },
    ],
  },
};

const PORTAL_ORDER = ["owner", "trustee", "rental", "contractor"];

const requirePortal = (portalKey) => {
  const portal = PORTALS[portalKey];

  if (!portal) {
    throw new Error(`Unsupported portal: ${portalKey}`);
  }

  return portal;
};

const findTab = (portal, candidate) => {
  if (!candidate) return null;

  return (
    portal.tabs.find(
      (tab) => tab.key === candidate || tab.value === candidate,
    ) || null
  );
};

const getRememberedTab = (portal) => {
  const remembered =
  sessionStorage.getItem(portal.storageKey) ||
  (portal.legacyStorageKey
    ? localStorage.getItem(portal.legacyStorageKey)
    : null);
  return findTab(portal, remembered) || portal.tabs[0] || null;
};

const getPortalDestination = (portal) => {
  const tab = getRememberedTab(portal);

  return tab
    ? `${portal.route}?tab=${encodeURIComponent(tab.key)}`
    : portal.route;
};

export const usePortalTab = (portalKey) => {
  const portal = requirePortal(portalKey);
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(() => {
    const requestedKey = new URLSearchParams(location.search).get("tab");
    const requestedTab = findTab(portal, requestedKey);

    return (requestedTab || getRememberedTab(portal))?.value || "";
  });

  useEffect(() => {
    const requestedKey = new URLSearchParams(location.search).get("tab");
    const requestedTab = findTab(portal, requestedKey);
    const nextTab = requestedTab || getRememberedTab(portal);

    if (!nextTab) return;

    setActiveTab(nextTab.value);
    sessionStorage.setItem(portal.storageKey, nextTab.value);

    if (!requestedTab) {
      navigate(`${portal.route}?tab=${encodeURIComponent(nextTab.key)}`, {
        replace: true,
      });
    }
  }, [location.search, navigate, portal]);

  const selectTab = useCallback(
    (candidate) => {
      const tab = findTab(portal, candidate);

      if (!tab) return;

      setActiveTab(tab.value);
      sessionStorage.setItem(portal.storageKey, tab.value);
      navigate(`${portal.route}?tab=${encodeURIComponent(tab.key)}`, {
        replace: true,
      });
    },
    [navigate, portal],
  );

  return [activeTab, selectTab];
};

const PortalSidebar = ({
  activePortal,
  activeTab,
  onTabChange,
  userAccess = [],
  loadingAccess = false,
  sidebarOpen,
  hiddenTabKeys = [],
  ticketRole,
  showBack = true,
  onLogout,
}) => {
  const activePortalConfig = requirePortal(activePortal);
  const navigate = useNavigate();
  const [showTicketModal, setShowTicketModal] = useState(false);

  const accessSet = useMemo(
    () =>
      new Set(
        (Array.isArray(userAccess) ? userAccess : []).map((access) =>
          String(access).toLowerCase(),
        ),
      ),
    [userAccess],
  );

  const hiddenTabs = useMemo(
    () => new Set(hiddenTabKeys),
    [hiddenTabKeys],
  );

  const resolvedTicketRole =
    ticketRole === undefined && ["owner", "trustee"].includes(activePortal)
      ? activePortal
      : ticketRole;

  const canViewPortal = (portalKey, portal) => {
    if (portalKey === activePortal) return true;
    if (loadingAccess) return false;

    return portal.access.some((access) => accessSet.has(access));
  };

  const selectPortalTab = (portalKey, portal, tab) => {
    sessionStorage.setItem(portal.storageKey, tab.value);

    const isCurrentPortalPage =
      portalKey === activePortal && typeof onTabChange === "function";

    if (isCurrentPortalPage) {
      onTabChange(tab.value);
      return;
    }

    navigate(`${portal.route}?tab=${encodeURIComponent(tab.key)}`);
  };

  const selectedTab =
    findTab(activePortalConfig, activeTab) ||
    getRememberedTab(activePortalConfig);

  return (
    <>
      <aside
        className={`${styles.sidebar} ${
          sidebarOpen ? styles.sidebarVisible : styles.sidebarHidden
        }`}
        style={{ position: "relative", alignSelf: "stretch" }}
      >
        {PORTAL_ORDER.map((portalKey) => {
          const portal = PORTALS[portalKey];

          if (!canViewPortal(portalKey, portal)) return null;

          const isActivePortal = portalKey === activePortal;

          return (
            <div className={styles.sidebarSection} key={portalKey}>
              {isActivePortal ? (
                <div
                  className={`${styles.sidebarSectionHead} ${styles.sidebarSectionHeadActive}`}
                >
                  <span>{portal.label}</span>
                  <span
                    className={`${styles.caretSelected} ${styles.caretUpSelected}`}
                  ></span>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.sidebarSectionHead}
                  onClick={() => navigate(getPortalDestination(portal))}
                >
                  <span>{portal.label}</span>
                  <span className={styles.caret}></span>
                </button>
              )}

              {isActivePortal &&
                portal.tabs
                  .filter((tab) => !hiddenTabs.has(tab.key))
                  .map((tab) => {
                    const isActiveTab = selectedTab?.key === tab.key;

                    return (
                      <button
                        type="button"
                        key={tab.key}
                        className={`${styles.sidebarItem} ${
                          isActiveTab ? styles.sidebarItemActive : ""
                        }`}
                        aria-current={isActiveTab ? "page" : undefined}
                        onClick={() =>
                          selectPortalTab(portalKey, portal, tab)
                        }
                      >
                        {tab.label}
                      </button>
                    );
                  })}
            </div>
          );
        })}

        <div
          className={styles.sidebarBottom}
          style={{ position: "absolute", bottom: "20px", left: 0, right: 0 }}
        >
          {resolvedTicketRole && (
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setShowTicketModal(true)}
            >
              Log a Ticket
            </button>
          )}

          {showBack && (
            <div className={styles.backLink} onClick={() => navigate(-1)}>
              Back
            </div>
          )}

          <div
            className={styles.backLink}
            onClick={() => (onLogout ? onLogout() : navigate("/#/"))}
          >
            Log Out
          </div>
        </div>
      </aside>

      {resolvedTicketRole && (
        <LogATicketModal
          open={showTicketModal}
          onClose={() => setShowTicketModal(false)}
          role={resolvedTicketRole}
        />
      )}
    </>
  );
};

export default PortalSidebar;